import { z } from 'zod'

import type { ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  PreProductionReviewSchema,
  ReferenceFileRequiredSchema,
  ServiceDomainSelectModelSchema,
  ServicePayoutSchema,
  ServiceTypeSchema,
} from './service.models.ts'
import { TagFilterSchema } from './tag.models.ts'
import {
  PackageIdSchema,
  PackageServiceIdSchema,
  ServiceIdSchema,
} from './unique-id.models.ts'

/**
 * Count
 */
export const PackageServiceCountSchema = z.int().positive()
export type PackageServiceCount = ZodInfer<typeof PackageServiceCountSchema>

/**
 * PackageServiceDomainSelectModel
 */
export const PackageServiceDomainSelectModelSchema =
  ServiceDomainSelectModelSchema.omit({ status: true }).extend({
    isArchived: z.boolean().optional(),
    packageServiceId: PackageServiceIdSchema,
    packageId: PackageIdSchema,
    serviceType: ServiceTypeSchema,
    cost: ServicePayoutSchema.optional(),
    count: PackageServiceCountSchema,
  })

export type PackageServiceDomainSelectModel = ZodInfer<
  typeof PackageServiceDomainSelectModelSchema
>

/**
 * PackageServiceDomainInsertModel
 */
export const PackageServiceDomainInsertModelSchema =
  PackageServiceDomainSelectModelSchema.extend({
    cost: PackageServiceDomainSelectModelSchema.shape.cost.nullable(),
    // Defaults only apply at insert time — base schemas are default-free
    // so that .partial() works correctly in update models.
    preProductionReview: PreProductionReviewSchema.prefault(false),
    preProductionBrandReferenceFileRequired:
      ReferenceFileRequiredSchema.prefault(false),
    preProductionMakerReferenceFileRequired:
      ReferenceFileRequiredSchema.prefault(false),
  }).partial({ packageServiceId: true })

export type PackageServiceDomainInsertModel = ZodInfer<
  typeof PackageServiceDomainInsertModelSchema
>

/**
 * PackageServiceDomainUpdateModel
 */
export const PackageServiceDomainUpdateModelSchema =
  PackageServiceDomainSelectModelSchema.pick({
    serviceType: true,
    count: true,
    name: true,
    description: true,
    max: true,
    isArchived: true,
    preProductionReview: true,
    preProductionBrandReferenceFileRequired: true,
    preProductionMakerReferenceFileRequired: true,
  })
    .extend({
      cost: PackageServiceDomainSelectModelSchema.shape.cost.nullable(),
    })
    .partial()

export type PackageServiceDomainUpdateModel = ZodInfer<
  typeof PackageServiceDomainUpdateModelSchema
>

/**
 * PackageServiceDomainSelectModelFilters
 */
export const PackageServiceDomainSelectModelFiltersSchema = z
  .object({
    isArchived: z.boolean(),
    packageServiceId: ModelFilterSchema(PackageServiceIdSchema),
    packageId: ModelFilterSchema(PackageIdSchema),
    serviceId: ModelFilterSchema(ServiceIdSchema),
    serviceType: ModelFilterSchema(ServiceTypeSchema),
    tags: TagFilterSchema,
    preProductionReview: PreProductionReviewSchema,
    preProductionBrandReferenceFileRequired: ReferenceFileRequiredSchema,
    preProductionMakerReferenceFileRequired: ReferenceFileRequiredSchema,
  })
  .partial()

export type PackageServiceDomainSelectModelFilters = ZodInfer<
  typeof PackageServiceDomainSelectModelFiltersSchema
>
